import { Suspense, lazy } from "react";
import type { PartialRouteObject } from "react-router";
import { Navigate } from "react-router-dom";
import AuthGuard from "../components/AuthGuard";
import BlogLayout from "../components/blog/BlogLayout";
import BrowseLayout from "../components/BrowseLayout";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import DocsLayout from "../components/docs/DocsLayout";
import GuestGuard from "../components/GuestGuard";
import LoadingScreen from "../components/LoadingScreen";
import MainLayout from "../components/MainLayout";

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
// Browse pages

const Browse = Loadable(lazy(() => import("../pages/browse/Browse")));
const BrowseButtons = Loadable(
  lazy(() => import("../pages/browse/BrowseButtons"))
);
const BrowseCharts = Loadable(
  lazy(() => import("../pages/browse/BrowseCharts"))
);
const BrowseColors = Loadable(
  lazy(() => import("../pages/browse/BrowseColors"))
);
const BrowseDetailLists = Loadable(
  lazy(() => import("../pages/browse/BrowseDetailLists"))
);
const BrowseForms = Loadable(lazy(() => import("../pages/browse/BrowseForms")));
const BrowseGridLists = Loadable(
  lazy(() => import("../pages/browse/BrowseGridLists"))
);
const BrowseGroupedLists = Loadable(
  lazy(() => import("../pages/browse/BrowseGroupedLists"))
);
const BrowseInputs = Loadable(
  lazy(() => import("../pages/browse/BrowseInputs"))
);
const BrowseModals = Loadable(
  lazy(() => import("../pages/browse/BrowseModals"))
);
const BrowseQuickStats = Loadable(
  lazy(() => import("../pages/browse/BrowseQuickStats"))
);
const BrowseTables = Loadable(
  lazy(() => import("../pages/browse/BrowseTables"))
);
const BrowseTypography = Loadable(
  lazy(() => import("../pages/browse/BrowseTypography"))
);

// Authentication pages

const Login = Loadable(
  lazy(() => import("../UIS_containers/pages/Authenticate/Login"))
);
const PasswordRecovery = Loadable(
  lazy(() => import("../UIS_containers/pages/Authenticate/PasswordRecovery"))
);
const PasswordReset = Loadable(
  lazy(() => import("../UIS_containers/pages/Authenticate/PasswordReset"))
);
const Register = Loadable(
  lazy(() => import("../UIS_containers/pages/Authenticate/Register"))
);
const VerifyCode = Loadable(
  lazy(() => import("../UIS_containers/pages/Authenticate/VerifyCode"))
);

// Blog pages

const BlogPostCreate = Loadable(
  lazy(() => import("../pages/blog/BlogPostCreate"))
);
const BlogPostDetails = Loadable(
  lazy(() => import("../pages/blog/BlogPostDetails"))
);
const BlogPostList = Loadable(lazy(() => import("../pages/blog/BlogPostList")));

// Dashboard pages

const Account = Loadable(lazy(() => import("../pages/dashboard/Account")));
const Analytics = Loadable(lazy(() => import("../pages/dashboard/Analytics")));
const Calendar = Loadable(lazy(() => import("../pages/dashboard/Calendar")));

const ProductListTable = Loadable(
  lazy(() => import("../pages/dashboard/ProductList"))
);
const MoreMenu = Loadable(lazy(() => import("../components/MoreMenu")));

const Chat = Loadable(lazy(() => import("../pages/dashboard/Chat")));
const CustomerDetails = Loadable(
  lazy(() => import("../pages/dashboard/CustomerDetails"))
);
const CustomerEdit = Loadable(
  lazy(() => import("../pages/dashboard/CustomerEdit"))
);
const CustomerList = Loadable(
  lazy(() => import("../pages/dashboard/CustomerList"))
);
const Finance = Loadable(lazy(() => import("../pages/dashboard/Finance")));
const InvoiceDetails = Loadable(
  lazy(() => import("../pages/dashboard/InvoiceDetails"))
);
const InvoiceList = Loadable(
  lazy(() => import("../pages/dashboard/InvoiceList"))
);
const Kanban = Loadable(lazy(() => import("../pages/dashboard/Kanban")));
const Mail = Loadable(lazy(() => import("../pages/dashboard/Mail")));
const OrderDetails = Loadable(
  lazy(() => import("../pages/dashboard/OrderDetails"))
);
const OrderList = Loadable(lazy(() => import("../pages/dashboard/OrderList")));
const Overview = Loadable(lazy(() => import("../pages/dashboard/Overview")));

// const Test = Loadable(
//     lazy(() => import("../UIS_containers/pages/Dashboard/Professors/Test"))
// );
const ProductCreate = Loadable(
  lazy(() => import("../pages/dashboard/ProductCreate"))
);
const ProductList = Loadable(
  lazy(() => import("../pages/dashboard/ProductList"))
);

// Dashboard pages student
const StudentInfo = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Students/StudentInfo/Student")
  )
);
const StudyProgram = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/StudyPrograms/StudyProgram"
      )
  )
);
const Decision = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Students/Decisions/Decision")
  )
);
const Equivalentcourse = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/Equivalentcourses/Equivalentcourse"
      )
  )
);
const Exam = Loadable(
  lazy(() => import("../UIS_containers/pages/Dashboard/Students/Exams/Exam"))
);
const BehaviorScoreTotal = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/BehaviorScoreTotals/BehaviorScoreTotal"
      )
  )
);
const AccountFee = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/AccountFees/AccountFee"
      )
  )
);
const Order = Loadable(
  lazy(() => import("../UIS_containers/pages/Dashboard/Students/Orders/Order"))
);
const KetQuaDangKyHP = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/KetQuaDangKyHP/KetQuaDangKyHP"
      )
  )
);
const MienGiam = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/ChinhSachMienGiam/MienGiam"
      )
  )
);
const Mark = Loadable(
  lazy(() => import("../UIS_containers/pages/Dashboard/Students/Marks/Mark"))
);
const DiemDanhSinhVien = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/DiemDanhSinhVien/DiemDanhSinhVien"
      )
  )
);
const Schedule = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Students/Schedules/Schedule")
  )
);
const Notification = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/Notifications/Notification"
      )
  )
);
const BehaviorScore = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/BehaviorScore/BehaviorScore"
      )
  )
);
const GhiDanhTuChon = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/GhiDanhTuChon/GhiDanhTuChon"
      )
  )
);
const GhiDanhNgoaiKeHoach = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/GhiDanhNgoaiKeHoach/GhiDanhNgoaiKeHoach"
      )
  )
);
const Comment = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Dashboard/Students/comments/Comment")
  )
);
const DangKyHocPhan = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/DangKyHocPhan/DangKyHocPhan"
      )
  )
);
const DangKyVangThi = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/DangKyVangThi/DangKyVangThi"
      )
  )
);
const LeTotNghiep = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/LeTotNghep/LeTotNghiep"
      )
  )
);
const HocBong = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Dashboard/Students/HocBong/HocBong")
  )
);
const NoiNgoaiTruSV = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/NoiNgoaiTruSV/NoiNgoaiTruSV"
      )
  )
);
const LienHe = Loadable(
  lazy(() => import("../UIS_containers/pages/Dashboard/Students/LienHe/LienHe"))
);
const Graduation = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Students/Graduation/Graduation")
  )
);
const DangKyChuyenNganh = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/DangKyChuyenNganh/DangKyChuyenNganh"
      )
  )
);
const ChungChiNgoaiNgu = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/ChungChiNgoaiNgu/ChungChiNgoaiNgu"
      )
  )
);
const CongTacXaHoi = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/CongTacXaHoi/CongTacXaHoi"
      )
  )
);
const BaoHiemYTe = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Students/BaoHiemYTe/BaoHiemYTe")
  )
);
const MuonPhong = Loadable(
    lazy(
      () =>
        import("../UIS_containers/pages/Dashboard/Students/MuonPhong/MuonPhong")
    )
  );
const RegistConfirm = Loadable(
    lazy(
        () =>
            import(
                "../UIS_containers/pages/Dashboard/Students/RegistConfirm/RegistConfirm"
            )
    )
);
// Docs pages

const Docs = Loadable(lazy(() => import("../pages/Docs")));

// Error pages

const AuthorizationRequired = Loadable(
  lazy(() => import("../UIS_containers/pages/Page401/AuthorizationRequired"))
);
const NotFound = Loadable(
  lazy(() => import("../UIS_containers/pages/Page404/NotFound"))
);
const ServerError = Loadable(
  lazy(() => import("../UIS_containers/pages/Page500/ServerError"))
);

// Projects pages

const ProjectBrowse = Loadable(
  lazy(() => import("../pages/dashboard/ProjectBrowse"))
);
const ProjectCreate = Loadable(
  lazy(() => import("../pages/dashboard/ProjectCreate"))
);
const ProjectDetails = Loadable(
  lazy(() => import("../pages/dashboard/ProjectDetails"))
);

// Social pages

const SocialFeed = Loadable(
  lazy(() => import("../pages/dashboard/SocialFeed"))
);
const SocialProfile = Loadable(
  lazy(() => import("../pages/dashboard/SocialProfile"))
);

// Other pages

const Checkout = Loadable(lazy(() => import("../pages/Checkout")));
const Contact = Loadable(lazy(() => import("../pages/Contact")));
const Home = Loadable(
  lazy(() => import("../UIS_containers/pages/Guest/Guest"))
);
const DanhSachPhong = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Guest/DanhSachPhong/DanhSachPhong")
  )
);
const TraCuuTKB = Loadable(
  lazy(() => import("../UIS_containers/pages/Guest/TraCuuTKB/TraCuuTKB"))
);
const TraCuuTKBGiangVien = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Guest/TraCuuTKBGiangVien/TraCuuTKBGiangVien"
      )
  )
);
const TraCuuTKBLopHP = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Guest/TraCuuTKBLopHP/TraCuuTKBLopHP")
  )
);
//
const Pricing = Loadable(lazy(() => import("../pages/Pricing")));

// professor
const SchedulesRegist = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/LopHocPhanDaDangKy/SchedulesRegist"
      )
  )
);
const SchedulesExam = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/LichCoiThi/SchedulesExam"
      )
  )
);
const TongHopGioGiangDay = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/TongHopGioGiangDay/TongHopGioGiangDay"
      )
  )
);
const DiemDanh = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Professors/DiemDanh/DiemDanh")
  )
);
const CauHinhTrongSo = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/CauHinhTrongSo/CauHinhTrongSo"
      )
  )
);
const CoVanHocTap = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/CoVanHocTap/CoVanHocTap"
      )
  )
);
const BaoNghiBaoBu = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/BaoNghiBaoBu/BaoNghiBaoBu"
      )
  )
);
const MuonPhongGV = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/MuonPhong/MuonPhong"
      )
  )
);
const ThoiKhoaBieuGiangVien = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/ThoiKhoaBieu/ThoiKhoaBieu"
      )
  )
);
const ThongTinCaNhanGiangVien = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Professors/ThongTinCaNhan/ThongTinCaNhan"
      )
  )
);
const ThongBaoProfessor = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Professors/ThongBao/ThongBao")
  )
);
//
//staff
const Functions = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Dashboard/Staff/Functions/Functions")
  )
);
const FunctionList = Loadable(
  lazy(
    () =>
      import("../UIS_containers/pages/Dashboard/Staff/Functions/FunctionList")
  )
);
const GroupFunctionRole = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/Functions/GroupFunctionRole"
      )
  )
);
const FunctionRoleStaff = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/Functions/FunctionRoleStaff"
      )
  )
);
const ThongKeSV = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Dashboard/Staff/ThongKeSV/ThongKeSV")
  )
);
const ThongKeNhapHoc = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeNhapHoc/ThongKeNhapHoc"
      )
  )
);
const ThongKeNhapHocTheoKhoi = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeNhapHoc/ThongKeTheoKhoi"
      )
  )
);
const ThongKeNhapHocTheoKhoiNganh = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeNhapHoc/ThongKeTheoKhoiNganh"
      )
  )
);
const DanhSachGiangVien = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/DanhSachGiangVien/DanhSachGiangVien"
      )
  )
);
const TimKiemSV = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/TimKiemSinhVien/TimKiemSinhVien"
      )
  )
);
const XemDiemLichHoc = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/XemDiemLichHoc/XemDiemLichHoc"
      )
  )
);
const TimPhongTrong = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/TimPhongTrong/TimPhongTrong"
      )
  )
);
const OverviewStaff = Loadable(
  lazy(() => import("../pages/dashboard/OverviewStaff"))
);

//test staff
const ThongKeNhapHocBieuDo = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeNhapHocBieuDo/ThongKeNhapHoc"
      )
  )
);
const ThongKeSinhVienBieuDo = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeThongTinSinhVienBieuDo/ThongKeSinhVien"
      )
  )
);

const ThongKeGiangVienBieuDo = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeThongTinGiangVienBieuDo/ThongKeGiangVien"
      )
  )
);

const ThongKeBienDongBieuDo = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ThongKeBienDongBieuDo/ThongKeBienDongBD"
      )
  )
);

const QuanLyNhomTin = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/QuanLyNhomTinTuc/QuanLyNhomTinTuc"
      )
  )
);
const QuanLyTinTuc = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/QuanLyTinTuc/QuanLyTinTuc"
      )
  )
);

const CapNhatTin = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/CapNhatThemTinTuc/ThemTinTuc"
      )
  )
);
const LienKet = Loadable(
  lazy(() => import("../UIS_containers/pages/Dashboard/Staff/LienKet/LienKet"))
);
const LichPhong = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Dashboard/Staff/LichPhong/LichPhong")
  )
);
const PhongHoc = Loadable(
  lazy(
    () => import("../UIS_containers/pages/Dashboard/Staff/PhongHoc/PhongHoc")
  )
);
const TraCuuMonHoc = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/TraCuuMonHoc/TraCuuMonHoc"
      )
  )
);
const TraCuuChuongTrinhDaoTao = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/TraCuuChuongTrinhDaoTao/TraCuuChuongTrinhDaoTao"
      )
  )
);
const KeHoachDaoTao = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/KeHoachDaoTao/KeHoachDaoTao"
      )
  )
);
const DanhSachKyLuat = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/DanhSachKyLuat/DanhSachKyLuat"
      )
  )
);
const QuanLyNgonNgu = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/QuanLyNgonNgu/QuanLyNgonNgu"
      )
  )
);
const HeaderMenus = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/HeaderMenus/HeaderMenus"
      )
  )
);
const ImportSchedule = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ImportSchedule/ImportSchedule"
      )
  )
);
const QuanLyMauDon = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/QuanLyMauDon/QuanLyMauDon"
      )
  )
);
const ConfigYearStudyPresent = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ConfigYearStudyPresent/ConfigYearStudyPresent"
      )
  )
);
const ConfirmRoomsRegisted = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/ConfirmRoomsRegisted/ConfirmRoomsRegisted"
      )
  )
);
const MuonPhongNV = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Staff/MuonPhongNV/MuonPhongNV"
      )
  )
);
//                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                       
const NotificationStaff = Loadable(
  lazy(
    () =>
      import(
        "../UIS_containers/pages/Dashboard/Students/Notifications/Notification"
      )
  )
);
const routes: PartialRouteObject[] = [
  {
    path: "login",
    element: (
      <GuestGuard>
        <Login />
      </GuestGuard>
    ),
  },
  {
    path: "authentication",
    children: [
      {
        path: "login",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },

      {
        path: "login-unguarded",
        element: <Login />,
      },
      {
        path: "password-recovery",
        element: <PasswordRecovery />,
      },
      {
        path: "password-reset",
        element: <PasswordReset />,
      },
      {
        path: "register",
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        ),
      },
      {
        path: "register-unguarded",
        element: <Register />,
      },
      {
        path: "verify-code",
        element: <VerifyCode />,
      },
    ],
  },
  {
    path: "blog",
    element: <BlogLayout />,
    children: [
      {
        path: "/",
        element: <BlogPostList />,
      },
      {
        path: "new",
        element: <BlogPostCreate />,
      },
      {
        path: ":postId",
        element: <BlogPostDetails />,
      },
    ],
  },
  {
    path: "contact",
    element: <Contact />,
  },
  //#region student routing

  {
    path: "student",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "/",
        element: <DanhSachPhong />,
      },
      {
        path: "info",
        element: <StudentInfo />,
      },
      {
        path: "StudyPrograms",
        element: <StudyProgram />,
      },
      {
        path: "Schedules",
        element: <Schedule />,
      },
      {
        path: "Exam",
        element: <Exam />,
      },
      {
        path: "Index",
        element: <Notification />,
      },
      {
        path: "Decisions",
        element: <Decision />,
      },
      {
        path: "DiemDanhSinhVien",
        element: <DiemDanhSinhVien />,
      },
      {
        path: "XemDiemRenLuyen",
        element: <BehaviorScoreTotal />,
      },
      {
        path: "marks",
        element: <Mark />,
      },
      {
        path: "AccountFees",
        element: <AccountFee />,
      },
      {
        path: "ChiTietHoaDon",
        element: <Order />,
      },
      {
        path: "KetQuaDangKy",
        element: <KetQuaDangKyHP />,
      },
      {
        path: "MienGiam",
        element: <MienGiam />,
      },
      {
        path: "HocPhanTuongDuong",
        element: <Equivalentcourse />,
      },
      {
        path: "DangKyVangThi",
        element: <DangKyVangThi />,
      },
      {
        path: "LeTotNghiep",
        element: <LeTotNghiep />,
      },
      {
        path: "DangKyChuyenNganh",
        element: <DangKyChuyenNganh />,
      },
      {
        path: "GhiDanhTuChon",
        element: <GhiDanhTuChon />,
      },
      {
        path: "GhiDanhNgoaiKeHoach",
        element: <GhiDanhNgoaiKeHoach />,
      },
      {
        path: "DangKyHocPhan",
        element: <DangKyHocPhan />,
      },
      {
        path: "Graduation",
        element: <Graduation />,
      },
      {
        path: "Comment",
        element: <Comment />,
      },
      {
        path: "BehaviorScore",
        element: <BehaviorScore />,
      },
      {
        path: "NoiNgoaiTruSV",
        element: <NoiNgoaiTruSV />,
      },
      {
        path: "LienHe",
        element: <LienHe />,
      },
      {
        path: "ChungChiNgoaiNgu",
        element: <ChungChiNgoaiNgu />,
      },
      {
        path: "https:/khdtkt.ueh.edu.vn/",
        // element: <Test name="a" />,
      },
      {
        path: "HocBong",
        element: <HocBong />,
      },
      {
        path: "CongTacXaHoi",
        element: <CongTacXaHoi />,
      },
      {
        path: "BaoHiemYTe",
        element: <BaoHiemYTe />,
      },
      {
        path: "MuonPhong",
        element: <MuonPhong />,
      },
      {
        path: "registconfirm",
        element: <RegistConfirm />,
    },
      {
        path: "TraCuuGiangDuong",
        element: <DanhSachPhong />,
      },
      {
        path: "TraCuuTKB",
        element: <TraCuuTKB />,
      },
      {
        path: "TraCuuTKBGiangVien",
        element: <TraCuuTKBGiangVien />,
      },
      {
        path: "TraCuuTKBLopHP",
        element: <TraCuuTKBLopHP />,
      },
      {
        path: "chat",
        children: [
          {
            path: "/",
            element: <Chat />,
          },
          {
            path: "new",
            element: <Chat />,
          },
          {
            path: ":threadKey",
            element: <Chat />,
          },
        ],
      },
      {
        path: "customers",
        children: [
          {
            path: "/",
            element: <CustomerList />,
          },
          {
            path: ":customerId",
            element: <CustomerDetails />,
          },
          {
            path: ":customerId/edit",
            element: <CustomerEdit />,
          },
        ],
      },
      {
        path: "invoices",
        children: [
          {
            path: "/",
            element: <InvoiceList />,
          },
          {
            path: ":invoiceId",
            element: <InvoiceDetails />,
          },
        ],
      },
      {
        path: "kanban",
        element: <Kanban />,
      },
      {
        path: "mail",
        children: [
          {
            path: "/",
            element: <Navigate to="/dashboard/mail/all" replace />,
          },
          {
            path: "label/:customLabel",
            element: <Mail />,
          },
          {
            path: "label/:customLabel/:emailId",
            element: <Mail />,
          },
          {
            path: ":systemLabel",
            element: <Mail />,
          },
          {
            path: ":systemLabel/:emailId",
            element: <Mail />,
          },
        ],
      },
      {
        path: "orders",
        children: [
          {
            path: "/",
            element: <OrderList />,
          },
          {
            path: ":orderId",
            element: <OrderDetails />,
          },
        ],
      },
      {
        path: "finance",
        element: <Finance />,
      },
      {
        path: "products",
        children: [
          {
            path: "/",
            element: <ProductList />,
          },
          {
            path: "new",
            element: <ProductCreate />,
          },
        ],
      },
      {
        path: "projects",
        children: [
          {
            path: "browse",
            element: <ProjectBrowse />,
          },
          {
            path: "new",
            element: <ProjectCreate />,
          },
          {
            path: ":projectId",
            element: <ProjectDetails />,
          },
        ],
      },
      {
        path: "social",
        children: [
          {
            path: "feed",
            element: <SocialFeed />,
          },
          {
            path: "profile",
            element: <SocialProfile />,
          },
        ],
      },
    ],
  },
  //#endregion
  //#region  routing professer
  {
    path: "professor",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: "/",
        element: <DanhSachPhong />,
      },
      {
        path: "info",
        element: <ThongTinCaNhanGiangVien />,
      },
      {
        path: "info1",
        element: <Account />,
      },
      {
        path: "regists",
        element: <SchedulesRegist />,
      },
      {
        path: "schedules",
        element: <ThoiKhoaBieuGiangVien />,
      },
      {
        path: "lichcoithigiangvien",
        element: <SchedulesExam />,
      },
      {
        path: "tonghopgiogiangday",
        element: <TongHopGioGiangDay />,
      },
      {
        path: "diemdanhonline",
        element: <DiemDanh />,
      },
      {
        path: "baonghibaobu",
        element: <BaoNghiBaoBu />,
      },
      {
        path: "professorclassstudents",
        element: <CoVanHocTap />,
      },
      {
        path: "MuonPhong",
        element: <MuonPhongGV />,
      },
      {
        path: "TraCuuGiangDuong",
        element: <DanhSachPhong />,
      },
      {
        path: "TraCuuTKB",
        element: <TraCuuTKB />,
      },
      {
        path: "TraCuuTKBGiangVien",
        element: <TraCuuTKBGiangVien />,
      },
      {
        path: "TraCuuTKBLopHP",
        element: <TraCuuTKBLopHP />,
      },
      {
        path: "chat",
        children: [
          {
            path: "/",
            element: <Chat />,
          },
          {
            path: "new",
            element: <Chat />,
          },
          {
            path: ":threadKey",
            element: <Chat />,
          },
        ],
      },
      {
        path: "customers",
        children: [
          {
            path: "/",
            element: <CustomerList />,
          },
          {
            path: ":customerId",
            element: <CustomerDetails />,
          },
          {
            path: ":customerId/edit",
            element: <CustomerEdit />,
          },
        ],
      },
      {
        path: "invoices",
        children: [
          {
            path: "/",
            element: <InvoiceList />,
          },
          {
            path: ":invoiceId",
            element: <InvoiceDetails />,
          },
        ],
      },
      {
        path: "index",
        element: <ThongBaoProfessor />,
      },
      {
        path: "inputmarks",
        element: <ProductCreate />,
      },
      {
        path: "inputmarkbyexamination",
        element: <ProductCreate />,
      },
      {
        path: "inputmarkbyexamination2",
        element: <ProductCreate />,
      },
      {
        path: "cauhinhtrongso",
        element: <CauHinhTrongSo />,
      },
      {
        path: "mail",
        children: [
          {
            path: "/",
            element: <Navigate to="/dashboard/mail/all" replace />,
          },
          {
            path: "label/:customLabel",
            element: <Mail />,
          },
          {
            path: "label/:customLabel/:emailId",
            element: <Mail />,
          },
          {
            path: ":systemLabel",
            element: <Mail />,
          },
          {
            path: ":systemLabel/:emailId",
            element: <Mail />,
          },
        ],
      },
      // {
      //   path: 'tonghopgiogiangday',
      //   children: [
      //     {
      //       path: '/',
      //       element: <OrderList />
      //     },
      //     {
      //       path: ':orderId',
      //       element: <OrderDetails />
      //     }
      //   ]
      // },
      {
        path: "finance",
        element: <Finance />,
      },
      {
        path: "products",
        children: [
          {
            path: "/",
            element: <ProductList />,
          },
          {
            path: "new",
            element: <ProductCreate />,
          },
        ],
      },
      {
        path: "projects",
        children: [
          {
            path: "browse",
            element: <ProjectBrowse />,
          },
          {
            path: "new",
            element: <ProjectCreate />,
          },
          {
            path: ":projectId",
            element: <ProjectDetails />,
          },
        ],
      },
      {
        path: "social",
        children: [
          {
            path: "feed",
            element: <SocialFeed />,
          },
          {
            path: "profile",
            element: <SocialProfile />,
          },
        ],
      },
    ],
  },
  //#endregion
  //#region routing employee
  {
    path: "staff",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),

    children: [
      {
        path: "/",
        element: <OverviewStaff />,
      },

      {
        path: "phanquyen",
        //element: <Functions />,
        children: [
          {
            path: "/",
            element: <Functions />,
          },
          {
            path: ":fnId",
            element: <Functions />,
          },
        ],
      },
      {
        path: "nhomquyen",
        element: <GroupFunctionRole />,
      },
      {
        path: "chucnang",
        element: <FunctionList />,
      },
      {
        path: "hanquyennhanvien",
        element: <Kanban />,
      },
      {
        path: "account",
        element: <Account />,
      },
      {
        path: "studentstatiscbyology",
        element: <ThongKeSV />,
      },
      {
        path: "studentattendedenrollsbynganh",
        element: <ThongKeNhapHoc />,
      },
      {
        path: "studentattendedenrollsbykhoi",
        element: <ThongKeNhapHocTheoKhoi />,
      },
      {
        path: "studentattendedenrollsbykhoinganh",
        element: <ThongKeNhapHocTheoKhoiNganh />,
      },
      {
        path: "searchstudent",
        element: <TimKiemSV />,
      },
      {
        path: "markschedules",
        element: <XemDiemLichHoc />,
      },
      {
        path: "listprofessors",
        element: <DanhSachGiangVien />,
      },
      {
        path: "capnhattin",
        children: [
          {
            path: "/",
            element: <CapNhatTin />,
          },
          {
            path: ":fnId",
            element: <CapNhatTin />,
          },
        ],
      },
      {
        path: "menus",
        element: <LienKet />,
      },
      {
        path: "roomsavailable",
        element: <TimPhongTrong />,
      },
      {
        path: "roomschedules",
        element: <LichPhong />,
      },
      {
        path: "getcampus",
        element: <PhongHoc />,
      },
      {
        path: "departmentsmanagingselcurriculum",
        element: <TraCuuMonHoc />,
      },
      {
        path: "coursesel",
        element: <TraCuuChuongTrinhDaoTao />,
      },
      {
        path: "studyprogramsel",
        element: <KeHoachDaoTao />,
      },
      {
        path: "graduatesstatistics",
        element: <Analytics />,
      },
      {
        path: "studentmissingschool",
        element: <ThongKeBienDongBieuDo />,
      },
      {
        path: "liststudentdiscipline",
        element: <DanhSachKyLuat />,
      },
      {
        path: "index",
        element: <NotificationStaff />,
      },
      {
        path: "letotnghiep",
        element: <ProductCreate />,
      },
      {
        path: "configwebsystem",
        element: <Browse />,
      },
      {
        path: "dotdangkynhanbangtn",
        element: <Calendar />,
      },
      {
        path: "congtacxahoi",
        element: <Calendar />,
      },
      {
        path: "dangkyvangthi",
        element: <Calendar />,
      },
      {
        path: "phanquyennhanvien",
        element: <FunctionRoleStaff />,
      },
      {
        path: "cauhinhemailphongban",
        element: <ProductCreate />,
      },
      {
        path: "cauhinhdangkyhuongdaotao",
        element: <ProductCreate />,
      },
      {
        path: "quanlynhomtin",
        element: <QuanLyNhomTin />,
      },
      {
        path: "quanlytintuc",
        element: <QuanLyTinTuc />,
      },
      {
        path: "thongbaoxettotnghiep",
        element: <Kanban />,
      },
      {
        path: "thongbaonopchungchi",
        element: <InvoiceDetails />,
      },
      {
        path: "thongkenhaphoc",
        element: <ThongKeNhapHocBieuDo />,
      },
      {
        path: "thongkesinhvien",
        element: <ThongKeSinhVienBieuDo />,
      },
      {
        path: "thongkegiangvien",
        element: <ThongKeGiangVienBieuDo />,
      },
      {
        path: "language",
        element: <QuanLyNgonNgu />,
      },
      {
        path: "HeaderMenus",
        element: <HeaderMenus />,
      },
      {
        path: "DuyetPhong",
        element: <ConfirmRoomsRegisted />,
      },
      {
        path: "MuonPhong",
        element: <MuonPhongNV />,
      },
      {
        path: "TraCuuGiangDuong",
        element: <DanhSachPhong />,
      },
      {
        path: "TraCuuTKB",
        element: <TraCuuTKB />,
      },
      {
        path: "TraCuuTKBGiangVien",
        element: <TraCuuTKBGiangVien />,
      },
      {
        path: "TraCuuTKBLopHP",
        element: <TraCuuTKBLopHP />,
      },
      {
        path: "ImportSchedule",
        element: <ImportSchedule />,
      },
      {
        path: "ConfigYearStudy",
        element: <ConfigYearStudyPresent />,
      },
      {
        path: "QuanLyMauDon",
        element: <QuanLyMauDon />,
      },
      // {
      //     path: "staff/index",
      //     element: <NotificationStaff />,
      // },
      {
        path: "sendmessage",
        children: [
          {
            path: "/",
            element: <Chat />,
          },
          {
            path: "new",
            element: <Chat />,
          },
          {
            path: ":threadKey",
            element: <Chat />,
          },
        ],
      },
      {
        path: "customers",
        children: [
          {
            path: "/",
            element: <CustomerList />,
          },
          {
            path: ":customerId",
            element: <CustomerDetails />,
          },
          {
            path: ":customerId/edit",
            element: <CustomerEdit />,
          },
        ],
      },
      {
        path: "invoices",
        children: [
          {
            path: "/",
            element: <InvoiceList />,
          },
          {
            path: ":invoiceId",
            element: <InvoiceDetails />,
          },
        ],
      },
      {
        path: "messagereceived?",
        element: <Kanban />,
      },
      {
        path: "mail",
        children: [
          {
            path: "/",
            element: <Navigate to="/dashboard/mail/all" replace />,
          },
          {
            path: "label/:customLabel",
            element: <Mail />,
          },
          {
            path: "label/:customLabel/:emailId",
            element: <Mail />,
          },
          {
            path: ":systemLabel",
            element: <Mail />,
          },
          {
            path: ":systemLabel/:emailId",
            element: <Mail />,
          },
        ],
      },
      {
        path: "orders",
        children: [
          {
            path: "/",
            element: <OrderList />,
          },
          {
            path: ":orderId",
            element: <OrderDetails />,
          },
        ],
      },
      {
        path: "finance",
        element: <Finance />,
      },
      {
        path: "products",
        children: [
          {
            path: "/",
            element: <ProductList />,
          },
          {
            path: "new",
            element: <ProductCreate />,
          },
        ],
      },
      {
        path: "projects",
        children: [
          {
            path: "browse",
            element: <ProjectBrowse />,
          },
          {
            path: "new",
            element: <ProjectCreate />,
          },
          {
            path: ":projectId",
            element: <ProjectDetails />,
          },
        ],
      },
      {
        path: "social",
        children: [
          {
            path: "feed",
            element: <SocialFeed />,
          },
          {
            path: "profile",
            element: <SocialProfile />,
          },
        ],
      },
    ],
  },
  //#endregion
  {
    path: "docs",
    element: <DocsLayout />,
    children: [
      {
        path: "/",
        element: <Navigate to="/docs/overview/welcome" replace />,
      },
      {
        path: "*",
        element: <Docs />,
      },
    ],
  },
  {
    // path: "*",
    // element: <MainLayout />,
    children: [
      {
        path: "/",
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: "TraCuuGiangDuong",
        element: <DanhSachPhong />,
      },
      {
        path: "TraCuuTKB",
        element: <TraCuuTKB />,
      },
      {
        path: "TraCuuTKBGiangVien",
        element: <TraCuuTKBGiangVien />,
      },
      {
        path: "TraCuuTKBLopHP",
        element: <TraCuuTKBLopHP />,
      },
      {
        path: "gioithieu",
        element: <MoreMenu />,
      },
      {
        path: "browse",
        element: <BrowseLayout />,
        children: [
          {
            path: "/",
            element: <Browse />,
          },
          {
            path: "/buttons",
            element: <BrowseButtons />,
          },
          {
            path: "/inputs",
            element: <BrowseInputs />,
          },
          {
            path: "/charts",
            element: <BrowseCharts />,
          },
          {
            path: "/colors",
            element: <BrowseColors />,
          },
          {
            path: "/data-display/detail-lists",
            element: <BrowseDetailLists />,
          },
          {
            path: "/data-display/quick-stats",
            element: <BrowseQuickStats />,
          },
          {
            path: "/data-display/tables",
            element: <BrowseTables />,
          },
          {
            path: "/forms",
            element: <BrowseForms />,
          },
          {
            path: "/modals",
            element: <BrowseModals />,
          },
          {
            path: "/lists/grouped-lists",
            element: <BrowseGroupedLists />,
          },
          {
            path: "/lists/grid-lists",
            element: <BrowseGridLists />,
          },
          {
            path: "/typography",
            element: <BrowseTypography />,
          },
        ],
      },
      {
        path: "checkout",
        element: <Checkout />,
      },
      {
        path: "pricing",
        element: <Pricing />,
      },
      {
        path: "401",
        element: <AuthorizationRequired />,
      },
      {
        path: "404",
        element: <NotFound />,
      },
      {
        path: "500",
        element: <ServerError />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
